import React, { useEffect, useState } from "react";
import { CiSearch } from "react-icons/ci";
import { RiDeleteBin6Line } from "react-icons/ri";
import { GoFileSymlinkFile } from "react-icons/go";
import axios from "axios";
import { Spin, Empty, Tooltip, Modal, message } from "antd";
import { useNavigate } from "react-router-dom";
import "../../styles/SystemLevelConfig.css";
import openNotification from "components/utils/CustomNotification";

const beAddress =
  window._env_?.REACT_APP_API_URL || process.env.REACT_APP_API_URL;

const { confirm } = Modal;

// ✅ Interface for HR info structure
interface HrInfoRecord {
  uid?: string;
  name: string;
  enable: string;
  base_url: string;
  token_endpoint: {
    enable: string;
    uri: string;
    query_params: Record<string, any>;
    response_type: string;
  };
  endpoints: {
    enable: string;
    uri: string;
    query_params: Record<string, any>;
    response_type: string;
  }[];
}

const SystemLevelConfig: React.FC = () => {
  const [configs, setConfigs] = useState<HrInfoRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  const sessionUid = localStorage.getItem("session_uid") || "";
  const userUid = localStorage.getItem("user_uid") || "";

  // ✅ Fetch Configs
  const fetchConfigs = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${beAddress}/api/hr-configs`, {
        headers: { "user-uid": userUid },
        withCredentials: true,
      });
      if (res.data?.status === "Success" && Array.isArray(res.data.data)) {
        setConfigs(res.data.data);
      } else {
        setConfigs([]);
      }
    } catch (err) {
      console.error("Error fetching system configs:", err);
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  };

  const deleteHrConfig = async (hrInfo: HrInfoRecord) => {
    try {
      setLoading(true);
      const res = await axios.delete(`${beAddress}/api/hr-info`, {
        headers: {
          "user-uid": userUid,
          "Content-Type": "application/json",
        }, withCredentials: true,
        data: hrInfo,
      });

      if (res.data?.status === "Success") {
        // Remove from UI
        setConfigs((prev) => prev.filter((cfg) => cfg.name !== hrInfo.name));

        // Show success notification
        openNotification({
          type: "success",
          message:
            res.data.message ||
            `Configuration "${hrInfo.name}" deleted successfully`,
        });
      } else {
        // Show failure notification
        openNotification({
          type: "error",
          message: res.data.message || "Failed to delete configuration",
        });
      }
    } catch (err: any) {
      console.error("Error deleting config:", err);
      openNotification({
        type: "error",
        message: err?.response?.data?.message || "Error deleting configuration",
      });
    } finally {
      setLoading(false);
    }
  };

  //  Styled Confirmation Modal
  const handleDelete = (hrInfo: HrInfoRecord) => {
    confirm({
      title: (
        <div style={{ textAlign: "center", fontWeight: 600 }}>
          Are you sure you want to delete this HR info?
        </div>
      ),
      icon: null,
      centered: true,
      okText: "Yes",
      cancelText: "No",
      okButtonProps: {
        style: {
          backgroundColor: "#d9534f",
          borderColor: "#d9534f",
          borderRadius: "6px",
          fontWeight: 500,
          minWidth: "70px",
        },
      },
      cancelButtonProps: {
        style: {
          backgroundColor: "#f0f0f0",
          color: "#000",
          borderRadius: "6px",
          fontWeight: 500,
          minWidth: "70px",
        },
      },
      okCancel: true,
      onOk() {
        deleteHrConfig(hrInfo);
      },
    });

    // Wait for modal to render and swap button order
    setTimeout(() => {
      const btnContainer = document.querySelector(".ant-modal-confirm-btns");
      if (btnContainer) {
        const buttons = btnContainer.children;
        if (buttons.length === 2) {
          btnContainer.insertBefore(buttons[1], buttons[0]); // Swap positions
        }
      }
    }, 0);
  };

  useEffect(() => {
    fetchConfigs();
  }, []);

  // ✅ Filter search
  const filteredConfigs = configs.filter((cfg) =>
    cfg.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="sysconfig-container">
      {/* Header */}
      <h2 className="sysconfig-header">HR Configuration</h2>

      {/* Controls */}
      <div className="sysconfig-controls">
        <div className="sysconfig-search-wrapper">
          <CiSearch className="sysconfig-search-icon" size={20} />
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="sysconfig-search-input"
          />
        </div>

        <button
          className="sysconfig-add-btn"
          onClick={() => navigate("/add-system-config")}
        >
          + Add HR Config
        </button>
      </div>

      {/* Loading / Empty / Data */}
      {loading ? (
        <div className="sysconfig-loading">
          <Spin size="large" />
        </div>
      ) : filteredConfigs.length === 0 ? (
        <Empty
          description={
            <span style={{ color: "#888" }}>No System Configs Found</span>
          }
          style={{ marginTop: 80 }}
        />
      ) : (
        <div className="sysconfig-grid">
          {filteredConfigs.map((cfg, idx) => (
            <div key={idx} className="sysconfig-card">
              {/* Header */}
              <div className="sysconfig-card-header">
                <h4 className="sysconfig-card-title">
                  {cfg.name || `Config ${idx + 1}`}
                </h4>

                <div className="sysconfig-card-actions">
                  <Tooltip title="View Configuration">
                    <button
                      className="sysconfig-view-btn"
                      onClick={() =>
                        navigate("/add-system-config", {
                          state: { mode: "view", config: cfg },
                        })
                      }
                    >
                      <GoFileSymlinkFile fontSize="18px" color="#000" />
                    </button>
                  </Tooltip>

                  <Tooltip title="Delete Configuration">
                    <button
                      className="sysconfig-delete-btn"
                      onClick={() => handleDelete(cfg)}
                    >
                      <RiDeleteBin6Line fontSize="18px" color="#b30000" />
                    </button>
                  </Tooltip>
                </div>
              </div>

              {/* Details */}
              <p className="sysconfig-detail">
                <strong>Enable:</strong>{" "}
                <span
                  className={
                    cfg.enable === "Yes"
                      ? "sysconfig-status-yes"
                      : "sysconfig-status-no"
                  }
                >
                  {cfg.enable === "Yes" ? "Yes" : "No"}
                </span>
              </p>
              <p className="sysconfig-detail">
                <strong>Base URL:</strong> {cfg.base_url || "—"}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SystemLevelConfig;
